import { useInfiniteQuery } from "@tanstack/react-query";
import { NewspaperIcon, StickyNote, Users2Icon, VideoIcon } from "lucide-react";
import { useMemo } from "react";

import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import { FeedAvatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import {
  getMonetizedFeedsQueryOptions,
  GetPublicFeedOptions,
  SearchResult,
} from "@/lib/api";
import { cn, formatBigNumber } from "@/lib/utils";

export function ResultGrid({
  search,
  sort,
  onSelect,
}: {
  search?: string;
  sort: GetPublicFeedOptions["sort_by"];
  onSelect: (feed: SearchResult) => void;
}) {
  const { data, fetchNextPage, isLoading } = useInfiniteQuery(
    getMonetizedFeedsQueryOptions({
      page: 1,
      page_size: 12,
      sort_by: sort,
      search,
    }),
  );

  const allResults = useMemo(() => {
    return data?.pages.flatMap((d) => d.results) || [];
  }, [data]);

  const total = data?.pages[0]?.total_items || 0;

  let items = null;

  if (allResults.length > 0) {
    items = allResults.map((item) => {
      return (
        <button
          type="button"
          onClick={() => onSelect(item)}
          key={item.algorithm.id}
          className={cn(
            "p-4",
            "flex flex-col items-stretch gap-4",
            "border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden",
          )}
        >
          <div className={cn("group text-left", "flex gap-4")}>
            <FeedAvatar
              feed={item.algorithm}
              className="max-w-[68px] h-full w-full"
            />
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <div className="flex flex-col flex-1">
                  <div className="text">{item.algorithm.display_name}</div>
                  <div className="text-xs text-slate-dim">@{item.username}</div>
                </div>
                {item.algorithm.is_video_feed && (
                  <SimpleTooltip message="Can only run video ads">
                    <div className="flex items-center justify-center gap-2 bg-greena-6 dark:bg-greendarka-6 h-6 w-6 rounded-lg">
                      <VideoIcon className="h-4 w-4" />
                    </div>
                  </SimpleTooltip>
                )}
                <div className="text-slate-dim text-sm bg-slate-3 dark:bg-slatedark-4 px-2 py-0.5 rounded-md flex">
                  ${((item.algorithm.cpm_cents ?? 1) / 100).toFixed(2)}
                </div>
              </div>
              <div className="flex gap-6 items-center">
                <SimpleTooltip message="Unique Users">
                  <div className="flex gap-1 items-center text-slate-dim text-sm">
                    <Users2Icon className="h-3 w-3" />
                    {formatBigNumber(
                      item.algorithm.stats.summary.avg_unique_user_count,
                    )}
                  </div>
                </SimpleTooltip>
                <SimpleTooltip message="Post Views">
                  <div className="flex gap-1 items-center text-slate-dim text-sm">
                    <StickyNote className="h-3 w-3" />
                    {formatBigNumber(
                      item.algorithm.stats.summary.avg_post_view_count,
                    )}
                  </div>
                </SimpleTooltip>
                <SimpleTooltip message="Feed Views">
                  <div className="flex gap-1 items-center text-slate-dim text-sm">
                    <NewspaperIcon className="h-3 w-3" />
                    {formatBigNumber(
                      item.algorithm.stats.summary.avg_feed_view_count,
                    )}
                  </div>
                </SimpleTooltip>
              </div>
            </div>
          </div>

          {item.algorithm.description && (
            <div className="text-left text-slate-dim text-sm">
              {item.algorithm.description}
            </div>
          )}

          {item.algorithm.algorithm_bio && (
            <div className="text-left text-slate-dim text-sm whitespace-pre-wrap">
              {item.algorithm.algorithm_bio}
            </div>
          )}
        </button>
      );
    });
  } else {
    items = Array.from({ length: 12 }).map((_, index) => {
      return (
        <Skeleton
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={"skeleton-" + index}
          className="group p-4 border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden"
          style={{ height: 180 }}
        />
      );
    });
  }

  return (
    <div className="grid">
      <div className="grid grid-cols-1 gap-4">{items}</div>
      {allResults.length < total && allResults.length > 0 && (
        <LoadMoreSpinner fetchNextPage={fetchNextPage} isLoading={isLoading} />
      )}
    </div>
  );
}
